import { getClientBuildCommit } from "@/lib/buildIdentity";
import { readRecentOperationsControl } from "@/lib/cleoOperationsControlContext";
import {
  extractHttpStatus,
  extractTraceId,
  isCustomerFacingPublicPath,
  normalizeSafeErrorCategory,
  redactStaffRoute,
} from "@shared/staffForegroundIssue";
import { toast, type ExternalToast } from "sonner";

export const TOAST_REPORT_OPERATION = "cleoOperationsDesk.reportToastError";

export type ToastErrorContext = {
  attemptedAction: string;
  operation: string;
  error: unknown;
  capturedAt: number;
};

export type ToastReportInput = {
  submissionKey: string;
  attemptedAction: string;
  operation: string;
  route: string;
  userVisibleMessage: string;
  safeErrorCategory: string;
  httpStatus: number | null;
  traceId: string | null;
  clientBuildCommit: string;
  occurredAt: string;
  viewport: { width: number; height: number };
  timezone: string;
  controlClicked?: { label: string; action: string | null } | null;
};

type ToastReportResult = { requestKey: string };
type ToastErrorReporterConfig = {
  submit: (input: ToastReportInput) => Promise<ToastReportResult>;
  open?: (path: string) => void;
  now?: () => Date;
  uuid?: () => string;
  showError?: typeof toast.error;
};

/**
 * An identical error toast (same visible message and same server trace) raised
 * again within this window is the same failure surfacing twice, so it reuses
 * the first toast and its single REPORT button (OPS-2026-0083/0084).
 */
export const TOAST_REPORT_DEDUPE_WINDOW_MS = 3_000;

let recentContext: ToastErrorContext | null = null;
let installed = false;
let lastShownError: { message: string; traceId: string; at: number; toastId: string | number } | null = null;
const originalToastError = toast.error.bind(toast) as typeof toast.error;

function safeText(value: unknown, fallback: string, max: number): string {
  const raw = typeof value === "string" || typeof value === "number" ? String(value) : fallback;
  return raw.replace(/https?:\/\/\S+/gi, "[redacted]").replace(/[\r\n\t]+/g, " ").trim().slice(0, max) || fallback;
}

function operationFromKey(key: unknown): string | null {
  if (!Array.isArray(key)) return null;
  const first = key[0];
  if (Array.isArray(first)) return first.map(String).join(".").slice(0, 128) || null;
  if (typeof first === "string") return first.slice(0, 128);
  return null;
}

export function trpcOperationFromCacheKey(key: unknown, fallback = "unknown"): string {
  return operationFromKey(key) ?? fallback;
}

export function rememberToastTrpcError(input: Omit<ToastErrorContext, "capturedAt">): void {
  if (input.operation === TOAST_REPORT_OPERATION) return;
  recentContext = { ...input, capturedAt: Date.now() };
}

export function resetToastErrorReporterForTests(): void {
  recentContext = null;
  installed = false;
  lastShownError = null;
  toast.error = originalToastError;
}

function currentContext(message: unknown): ToastErrorContext {
  const recent = recentContext;
  if (recent && Date.now() - recent.capturedAt <= 5_000 && recent.operation !== TOAST_REPORT_OPERATION) {
    recentContext = null;
    return recent;
  }
  const visible = safeText(message, "An unexpected error occurred", 1000);
  return {
    attemptedAction: visible,
    operation: "client.toast.error",
    error: {},
    capturedAt: Date.now(),
  };
}

export function buildToastReportInput(input: {
  message: unknown;
  context: ToastErrorContext;
  submissionKey: string;
  now?: Date;
  route?: string;
  viewport?: { width: number; height: number };
  timezone?: string;
  clientBuildCommit?: string;
}): ToastReportInput {
  const route = redactStaffRoute(input.route ?? (typeof window !== "undefined" ? window.location.pathname : "/"));
  return {
    submissionKey: input.submissionKey,
    attemptedAction: safeText(input.context.attemptedAction, "Staff action", 255),
    operation: safeText(input.context.operation, "unknown", 128).replace(/[^A-Za-z0-9_.:-]/g, "_"),
    route,
    userVisibleMessage: safeText(input.message, "An unexpected error occurred", 1000),
    safeErrorCategory: normalizeSafeErrorCategory(input.context.error),
    httpStatus: extractHttpStatus(input.context.error),
    traceId: extractTraceId(input.context.error),
    clientBuildCommit: safeText(input.clientBuildCommit ?? getClientBuildCommit(), "unknown", 64),
    occurredAt: (input.now ?? new Date()).toISOString(),
    viewport: input.viewport ?? {
      width: typeof window !== "undefined" ? window.innerWidth : 0,
      height: typeof window !== "undefined" ? window.innerHeight : 0,
    },
    timezone: safeText(input.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone, "unknown", 64),
    controlClicked: (() => {
      const control = readRecentOperationsControl();
      return control
        ? { label: safeText(control.label, "unknown", 120), action: control.action }
        : null;
    })(),
  };
}

/**
 * Installs one global wrapper around Sonner's error toast. The reporting call is
 * deliberately made outside React Query so its failure cannot recurse into a
 * second report toast, and a retry always reuses the same submission UUID.
 */
export function installToastErrorReporter(config: ToastErrorReporterConfig): void {
  if (installed) return;
  installed = true;
  const showError = config.showError ?? originalToastError;
  const open = config.open ?? ((path: string) => window.location.assign(path));
  const uuid = config.uuid ?? (() => crypto.randomUUID());
  const now = config.now ?? (() => new Date());

  toast.error = ((message: Parameters<typeof toast.error>[0], options?: ExternalToast) => {
    const route = redactStaffRoute(typeof window !== "undefined" ? window.location.pathname : "/");
    if (isCustomerFacingPublicPath(route)) return showError(message, options);

    const context = currentContext(message);
    const submissionKey = uuid();
    const reportInput = buildToastReportInput({ message, context, submissionKey, now: now(), route });
    const shownAt = Date.now();
    const previous = lastShownError;
    if (
      reportInput.traceId &&
      previous &&
      previous.traceId === reportInput.traceId &&
      previous.message === reportInput.userVisibleMessage &&
      shownAt - previous.at <= TOAST_REPORT_DEDUPE_WINDOW_MS
    ) {
      return previous.toastId;
    }
    let toastId: string | number;
    let reporting = false;

    const submit = async () => {
      if (reporting) return;
      reporting = true;
      showError(message, {
        ...options,
        id: toastId,
        duration: Infinity,
        action: { label: "REPORTING…", onClick: () => undefined },
      });
      try {
        const result = await config.submit(reportInput);
        showError(`Reported as ${result.requestKey}`, {
          id: toastId,
          duration: Infinity,
          description: `${reportInput.userVisibleMessage} Click SEE REPORT to follow this with Cleo.`,
          action: {
            label: "SEE REPORT",
            onClick: () => open(`/cleo/operations/${encodeURIComponent(result.requestKey)}`),
          },
        });
      } catch {
        reporting = false;
        showError(message, {
          ...options,
          id: toastId,
          duration: Infinity,
          description: "The error report could not be sent. The original action was not retried.",
          action: { label: "RETRY REPORT", onClick: () => void submit() },
        });
      }
    };

    toastId = showError(message, {
      ...options,
      duration: Infinity,
      action: { label: "REPORT", onClick: () => void submit() },
    });
    lastShownError = reportInput.traceId
      ? { message: reportInput.userVisibleMessage, traceId: reportInput.traceId, at: shownAt, toastId }
      : null;
    return toastId;
  }) as typeof toast.error;
}
